import { jwtDecode } from "jwt-decode";
import axios from "axios";

import config from "../../config";

// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.baseURL = config.apiPrefix;

// intercepting to capture errors
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;

    if (error && error.response && error.response.status === 404) {
      // window.location.href = '/not-found';
    } else if (error && error.response && error.response.status === 403) {
      window.location.href = "/access-denied";
    } else {
      switch (error.response.status) {
        case 401:
          message = "Please authenticate";
          break;
        case 422:
          message = error.response.data.errors;
          break;
        case 403:
          message = "Access Forbidden";
          break;
        case 404:
          message = "Sorry! the data you are looking for could not be found";
          break;
        default: {
          message =
            error.response && error.response.data
              ? error.response.data["message"]
              : error.message || error;
        }
      }
      return Promise.reject({ error: message, status: error.response.status });
    }
  }
);

const AUTH_SESSION_KEY = "furrfernds_user";

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token) => {
  if (token) axios.defaults.headers.common["Authorization"] = "Bearer " + token;
  else delete axios.defaults.headers.common["Authorization"];
};

const getUserFromCookie = () => {
  const user = sessionStorage.getItem(AUTH_SESSION_KEY);
  return user ? (typeof user == "object" ? user : JSON.parse(user)) : null;
};

class APICore {
  /**
   * Fetches data from given url
   */
  get = (url, params) => {
    let response;
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] === "") delete params[key];
      });
      var queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }
    return response;
  };

  getById = (url) => {
    let response;

    response = axios.get(`${url}`);

    return response;
  };

  getFile = (url, params) => {
    let response;
    if (params) {
      var queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
      response = axios.get(`${url}?${queryString}`, { responseType: "blob" });
    } else {
      response = axios.get(`${url}`, { responseType: "blob" });
    }
    return response;
  };

  getMultiple = (urls, params) => {
    const reqs = [];
    let queryString = "";
    if (params) {
      queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";
    }

    for (const url of urls) {
      reqs.push(axios.get(`${url}?${queryString}`));
    }
    return axios.all(reqs);
  };

  /**
   * post given data to url
   */
  create = (url, data, headers = {}) => {
    const request = axios.post(url, data, headers);
    return request;
  };

  /**
   * post given data to url
   */
  fetchArrayBufferPost = (url, data) => {
    const request = axios.post(url, data, {
      responseType: "arraybuffer",
    });
    return request;
  };

  /**
   * Updates patch data
   */
  updatePatch = (url, data) => {
    return axios.patch(url, data);
  };

  /**
   * Updates data
   */
  update = (url, data) => {
    return axios.put(url, data);
  };

  /**
   * Deletes data
   */
  delete = (url) => {
    return axios.delete(url);
  };

  /**
   * post given data to url with file
   */
  createWithFile = (url, data) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }
    const config = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.post(url, formData, config);
  };

  /**
   * post given data to url with file
   */
  updateWithFile = (url, data) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }
    const config = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.patch(url, formData, config);
  };

  /**
   * post given data to url with file
   */
  uploadFile = (url, data) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }
    const config = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.post(url, formData, config);
  };
  /**
   * post given data to url with files
   */
  uploadMultipleFile = (url, formData) => {
    const config = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.post(url, formData, config);
  };
  /**
   * post given data to url with file
   */
  importFile = (url, data) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }
    const config = {
      headers: {
        "Content-Type": "application/vnd.ms-excel",
        Authorization: axios.defaults.headers.common["Authorization"],
      },
    };
    return axios.post(url, formData, config);
  };

  isUserAuthenticated = () => {
    const user = this.getLoggedInUser();

    if (!user) {
      return false;
    }
    const decoded = jwtDecode(user.token);
    // const decoded: any = jwtDecode(user.token);
    const currentTime = Date.now() / 1000;
    if (decoded.exp < currentTime) {
      console.warn("access token expired");
      axios
        .post("/auth/refresh-tokens", { refreshToken: user.refreshToken })
        .then((res) => {
          const tokens = res.data;
          const userWithToken = {
            ...user,
            token: tokens.access.token,
            refreshToken: tokens.refresh.token,
          };
          this.setLoggedInUser(userWithToken);
          setAuthorization(tokens.access.token);
        });
      return false;
    } else {
      return true;
    }
  };

  setLoggedInUser = (session) => {
    if (session)
      sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    else {
      sessionStorage.removeItem(AUTH_SESSION_KEY);
    }
  };

  getCurrentProfileStep = () => {
    let currentStep = sessionStorage.getItem("CurrentProfileStep");
    currentStep = JSON.parse(currentStep);
    if (currentStep) {
      return currentStep;
    } else {
      return "PetUserDetails";
    }
  };
  setCurrentProfileStep = (session) => {
    if (session)
      sessionStorage.setItem("CurrentProfileStep", JSON.stringify(session));
    else {
      sessionStorage.removeItem("CurrentProfileStep");
    }
  };
  setCurrentPetProfile = (session) => {
    if (session)
      sessionStorage.setItem("CurrentPetProfile", JSON.stringify(session));
    else {
      sessionStorage.removeItem("CurrentPetProfile");
    }
  };

  getCurrentPetProfile = () => {
    let currentProfile = sessionStorage.getItem("CurrentPetProfile");
    currentProfile = JSON.parse(currentProfile);
    if (currentProfile) {
      return currentProfile;
    } else {
      return {};
    }
  };

  setCartItems = (session) => {
    if (session) sessionStorage.setItem("cartItems", JSON.stringify(session));
    else {
      sessionStorage.removeItem("cartItems");
    }
  };

  getCartItems = () => {
    let cartItems = sessionStorage.getItem("cartItems");
    cartItems = JSON.parse(cartItems);
    if (cartItems) {
      return cartItems;
    } else {
      return [];
    }
  };

  /**
   * Returns the logged in user
   */
  getLoggedInUser = () => {
    return getUserFromCookie();
  };

  setUserInSession = (modifiedUser) => {
    let userInfo = sessionStorage.getItem(AUTH_SESSION_KEY);
    if (userInfo) {
      const { token, user } = JSON.parse(userInfo);
      this.setLoggedInUser({ token, ...user, ...modifiedUser });
    }
  };
}

/*
Check if token available in session
*/
let user = getUserFromCookie();
if (user) {
  const { token } = user;
  if (token) {
    setAuthorization(token);
  }
}

export { APICore, setAuthorization };
